/**
 * WeeksOverview.jsx - Sprint 2
 * 
 * Vista compacta de las 18 semanas del curso grupal.
 * Muestra progreso de configuración y permite abrir editor por semana.
 * 
 * @see PLAN_GROUPCOURSE_BUILDER.md
 */

import React from 'react';
import { 
  Calendar,
  CheckCircle2,
  AlertCircle,
  Edit,
  Copy,
  Circle
} from 'lucide-react';

const WeeksOverview = ({ course, onEditWeek, onDuplicateWeek }) => {
  const totalWeeks = course?.groupCourseConfig?.totalWeeks || 18;
  const configuredWeeks = course?.weeks || [];
  
  // Crear array de 18 semanas con data o vacío
  const weeksData = Array.from({ length: totalWeeks }, (_, index) => {
    const weekNumber = index + 1;
    const weekData = configuredWeeks.find(w => w.weekNumber === weekNumber);
    return {
      weekNumber,
      configured: !!weekData,
      data: weekData || null
    };
  });
  
  // Calcular completitud de una semana
  const getWeekCompleteness = (weekData) => {
    if (!weekData) return 0;
    
    let score = 0;
    const checks = [
      weekData.b1_resources?.length > 0,           // 20%
      weekData.b2_execution_template?.fields?.length > 0, // 30% (mandatory)
      weekData.b3_criteria?.length > 0,            // 15%
      weekData.b4_coach_config?.enabled !== false, // 10%
      weekData.b5_formalization_template?.sections?.length > 0, // 10%
      weekData.b6_criteria?.length > 0             // 15%
    ];
    
    const weights = [20, 30, 15, 10, 10, 15];
    checks.forEach((check, idx) => {
      if (check) score += weights[idx];
    });
    
    return score;
  };
  
  // Verificar si tiene errores críticos
  const hasErrors = (weekData) => {
    if (!weekData) return false;
    
    // Error: No tiene template B2 (mandatory)
    if (!weekData.b2_execution_template?.fields || weekData.b2_execution_template.fields.length === 0) {
      return true;
    }
    
    // Error: B3 pesos no suman 100%
    if (weekData.b3_criteria?.length > 0) {
      const b3Sum = weekData.b3_criteria.reduce((sum, c) => sum + (c.weight || 0), 0);
      if (b3Sum !== 100) return true;
    }
    
    // Error: B6 pesos no suman 100%
    if (weekData.b6_criteria?.length > 0) {
      const b6Sum = weekData.b6_criteria.reduce((sum, c) => sum + (c.weight || 0), 0);
      if (b6Sum !== 100) return true;
    }
    
    return false;
  };
  
  const getWeekStatusIcon = (week) => {
    if (!week.configured) {
      return <Circle className="w-5 h-5 text-gray-300" />;
    }
    
    const completeness = getWeekCompleteness(week.data);
    const errors = hasErrors(week.data);
    
    if (errors) {
      return <AlertCircle className="w-5 h-5 text-red-500" />;
    }
    
    if (completeness === 100) {
      return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    }
    
    return <AlertCircle className="w-5 h-5 text-yellow-500" />;
  };
  
  const getWeekStatusColor = (week) => {
    if (!week.configured) return 'border-gray-200 bg-gray-50';
    
    const completeness = getWeekCompleteness(week.data);
    const errors = hasErrors(week.data);
    
    if (errors) return 'border-red-200 bg-red-50';
    if (completeness === 100) return 'border-green-200 bg-green-50';
    return 'border-yellow-200 bg-yellow-50';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-600" />
            Configuración de 18 Semanas
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {configuredWeeks.length} de {totalWeeks} semanas configuradas
          </p>
        </div>
        
        {/* Stats */}
        <div className="flex gap-6 text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <span className="text-gray-700">
              Completas: <span className="font-semibold">{weeksData.filter(w => getWeekCompleteness(w.data) === 100).length}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-gray-700">
              Con errores: <span className="font-semibold">{weeksData.filter(w => hasErrors(w.data)).length}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Grid de semanas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {weeksData.map((week) => {
          const completeness = getWeekCompleteness(week.data);
          const errors = hasErrors(week.data);
          
          return (
            <div
              key={week.weekNumber}
              className={`border-2 rounded-lg p-4 transition-all hover:shadow-md ${getWeekStatusColor(week)}`}
            >
              {/* Header de semana */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {getWeekStatusIcon(week)}
                  <span className="font-semibold text-gray-900">
                    Semana {week.weekNumber}
                  </span>
                </div>
                <div className="text-xs font-medium text-gray-600">
                  {completeness}%
                </div>
              </div>

              {/* Barra de progreso */}
              <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                <div
                  className={`h-2 rounded-full transition-all ${
                    errors ? 'bg-red-500' : 
                    completeness === 100 ? 'bg-green-500' : 
                    completeness > 0 ? 'bg-yellow-500' : 'bg-gray-300'
                  }`}
                  style={{ width: `${completeness}%` }}
                />
              </div>

              {/* Bloques configurados */}
              <div className="grid grid-cols-6 gap-1 mb-3">
                {['B1', 'B2', 'B3', 'B4', 'B5', 'B6'].map((block, idx) => {
                  const blockKey = block.toLowerCase();
                  let isConfigured = false;
                  
                  if (week.data) {
                    switch (blockKey) {
                      case 'b1':
                        isConfigured = week.data.b1_resources?.length > 0;
                        break;
                      case 'b2':
                        isConfigured = week.data.b2_execution_template?.fields?.length > 0;
                        break;
                      case 'b3':
                        isConfigured = week.data.b3_criteria?.length > 0;
                        break;
                      case 'b4':
                        isConfigured = week.data.b4_coach_config?.enabled !== false;
                        break;
                      case 'b5':
                        isConfigured = week.data.b5_formalization_template?.sections?.length > 0;
                        break;
                      case 'b6':
                        isConfigured = week.data.b6_criteria?.length > 0;
                        break;
                    }
                  }
                  
                  return (
                    <div
                      key={block}
                      className={`text-xs font-medium py-1 px-1 rounded text-center ${
                        isConfigured 
                          ? 'bg-purple-600 text-white' 
                          : 'bg-gray-200 text-gray-500'
                      }`}
                      title={block}
                    >
                      {block}
                    </div>
                  );
                })}
              </div>

              {/* Errores */}
              {errors && (
                <div className="text-xs text-red-600 mb-3 flex items-start gap-1">
                  <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>Requiere correcciones</span>
                </div>
              )}

              {/* Acciones */}
              <div className="flex gap-2">
                <button
                  onClick={() => onEditWeek(week.weekNumber)}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-xs py-2 px-3 rounded-lg flex items-center justify-center gap-1 transition-colors"
                >
                  <Edit className="w-3 h-3" />
                  {week.configured ? 'Editar' : 'Configurar'}
                </button>
                {week.configured && (
                  <button
                    onClick={() => onDuplicateWeek(week.weekNumber)}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs py-2 px-3 rounded-lg transition-colors"
                    title="Duplicar semana"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Leyenda */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Leyenda de Bloques</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs text-gray-700">
          <div><span className="font-semibold">B1:</span> Recursos (videos, PDFs, links)</div>
          <div><span className="font-semibold">B2:</span> Template ejecución (obligatorio)</div>
          <div><span className="font-semibold">B3:</span> Criterios auditoría</div>
          <div><span className="font-semibold">B4:</span> Configuración Coach IA</div>
          <div><span className="font-semibold">B5:</span> Template formalización</div>
          <div><span className="font-semibold">B6:</span> Criterios cross-feedback</div>
        </div>
      </div>
    </div>
  );
};

export default WeeksOverview;
